import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  ChevronLeft, ChevronRight, CheckCircle2, XCircle, FileText, Palmtree,
  CalendarClock, Coffee, Download, Loader2, Plus, Trash2, Check, Settings2,
  ListChecks, UserCheck, X
} from 'lucide-react';
import axios from 'axios';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Status Configuration ──
const STATUS_CONFIG = {
  present: {
    label: 'Présent', icon: CheckCircle2,
    bg: 'bg-emerald-50', border: 'border-emerald-500', text: 'text-emerald-700',
    iconColor: 'text-emerald-500', cellClass: 'bg-emerald-50 border-emerald-400',
  },
  absent: {
    label: 'Absent', icon: XCircle,
    bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700',
    iconColor: 'text-red-500', cellClass: 'bg-red-50 border-red-400',
  },
  justified: {
    label: 'Justifié', icon: FileText,
    bg: 'bg-amber-50', border: 'border-amber-500', text: 'text-amber-700',
    iconColor: 'text-amber-500', cellClass: 'bg-amber-50 border-amber-400',
  },
  holiday: {
    label: 'Férié', icon: Palmtree,
    bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700',
    iconColor: 'text-orange-500', cellClass: 'bg-orange-50 border-orange-400',
  },
  planned: {
    label: 'Planifié', icon: CalendarClock,
    bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-700',
    iconColor: 'text-blue-500', cellClass: 'bg-blue-50 border-blue-400',
  },
  rest: {
    label: 'Repos', icon: Coffee,
    bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-500',
    iconColor: 'text-gray-400', cellClass: 'bg-gray-50 border-gray-200',
  },
};

const ABSENCE_REASONS = [
  { value: 'panne', label: 'Panne mécanique' },
  { value: 'maintenance', label: 'Maintenance préventive' },
  { value: 'accident', label: 'Accident' },
  { value: 'chauffeur', label: 'Absence chauffeur' },
  { value: 'meteo', label: 'Conditions météo' },
  { value: 'autre', label: 'Autre (personnalisé)' },
];

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];
const DAYS_SHORT = ['L', 'M', 'Me', 'J', 'V', 'S', 'D'];

function getMonthStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
function getDayOfWeek(dateStr) {
  return new Date(dateStr + 'T00:00:00').getDay();
}

const AttendanceCalendar = ({ contractId, contract }) => {
  const [currentMonth, setCurrentMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);

  // Absence dialog
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({ reason: 'panne', customReason: '', justification: '' });
  const [submittingAbsence, setSubmittingAbsence] = useState(false);

  // Batch mode
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelection, setBatchSelection] = useState(new Set()); // "slotId|date"
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchForm, setBatchForm] = useState({ reason: 'panne', customReason: '', justification: '' });
  const [submittingBatch, setSubmittingBatch] = useState(false);

  // Manual presence dialog
  const [showPresenceDialog, setShowPresenceDialog] = useState(false);
  const [presenceForm, setPresenceForm] = useState({ vehiclePlate: '', note: '' });
  const [submittingPresence, setSubmittingPresence] = useState(false);

  // Holiday management
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [holidayForm, setHolidayForm] = useState({ date: '', label: '', recurring: false });
  const [loadingHolidays, setLoadingHolidays] = useState(false);

  const monthStr = getMonthStr(currentMonth);
  const headers = { Authorization: 'Bearer cookie-auth' };

  // ── Fetch attendance data ──
  const fetchAttendance = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/contracts/${contractId}/attendance?month=${monthStr}`, { headers });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [contractId, monthStr]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  // ── Fetch holidays ──
  const fetchHolidays = useCallback(async () => {
    setLoadingHolidays(true);
    try {
      const res = await axios.get(`${API}/holidays?year=${currentMonth.getFullYear()}`, { headers });
      setHolidays(res.data.holidays || []);
    } catch (err) {
      console.error('Holiday fetch error:', err);
    } finally {
      setLoadingHolidays(false);
    }
  }, [currentMonth]);

  // ── Navigation ──
  const prevMonth = () => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)); setBatchMode(false); setBatchSelection(new Set()); };
  const nextMonth = () => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)); setBatchMode(false); setBatchSelection(new Set()); };

  // ── Cell click ──
  const handleCellClick = (slot, day) => {
    if (day.status === 'rest' || day.status === 'holiday') return;
    if (batchMode) {
      // Toggle selection for absent/justified cells
      const key = `${slot.slotId}|${day.date}`;
      setBatchSelection(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      return;
    }
    setSelectedCell({ slot, day });
  };

  // ── Single absence CRUD ──
  const handleDeclareAbsence = async () => {
    if (!selectedCell) return;
    const finalReason = absenceForm.reason === 'autre' ? (absenceForm.customReason || 'Autre') : absenceForm.reason;
    setSubmittingAbsence(true);
    try {
      const plate = selectedCell.slot.vehicles?.[0] || 'INCONNU';
      await axios.post(`${API}/contracts/${contractId}/absences`, {
        vehiclePlate: plate,
        slotId: selectedCell.slot.slotId,
        date: selectedCell.day.date,
        reason: finalReason,
        justification: absenceForm.justification,
      }, { headers });
      setShowAbsenceDialog(false);
      setAbsenceForm({ reason: 'panne', customReason: '', justification: '' });
      setSelectedCell(null);
      fetchAttendance();
    } catch (err) {
      console.error('Absence error:', err);
    } finally {
      setSubmittingAbsence(false);
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    try {
      await axios.delete(`${API}/contracts/${contractId}/absences/${absenceId}`, { headers });
      setSelectedCell(null);
      fetchAttendance();
    } catch (err) {
      console.error('Delete absence error:', err);
    }
  };

  // ── Batch absences ──
  const handleBatchAbsences = async () => {
    if (batchSelection.size === 0) return;
    const finalReason = batchForm.reason === 'autre' ? (batchForm.customReason || 'Autre') : batchForm.reason;
    setSubmittingBatch(true);

    // Group by unique slotIds and dates
    const slotIds = new Set();
    const dates = new Set();
    batchSelection.forEach(key => {
      const [sid, date] = key.split('|');
      slotIds.add(sid);
      dates.add(date);
    });

    try {
      await axios.post(`${API}/contracts/${contractId}/absences/batch`, {
        slotIds: [...slotIds],
        dates: [...dates],
        reason: finalReason,
        justification: batchForm.justification || '',
      }, { headers });
      setShowBatchDialog(false);
      setBatchForm({ reason: 'panne', customReason: '', justification: '' });
      setBatchSelection(new Set());
      setBatchMode(false);
      fetchAttendance();
    } catch (err) {
      console.error('Batch error:', err);
    } finally {
      setSubmittingBatch(false);
    }
  };

  // ── Manual presence ──
  const handleDeclarePresence = async () => {
    if (!selectedCell) return;
    setSubmittingPresence(true);
    try {
      await axios.post(`${API}/contracts/${contractId}/manual-presences`, {
        slotId: selectedCell.slot.slotId,
        date: selectedCell.day.date,
        vehiclePlate: presenceForm.vehiclePlate || '',
        note: presenceForm.note || '',
      }, { headers });
      setShowPresenceDialog(false);
      setPresenceForm({ vehiclePlate: '', note: '' });
      setSelectedCell(null);
      fetchAttendance();
    } catch (err) {
      console.error('Presence error:', err);
    } finally {
      setSubmittingPresence(false);
    }
  };

  const handleDeletePresence = async (presenceId) => {
    try {
      await axios.delete(`${API}/contracts/${contractId}/manual-presences/${presenceId}`, { headers });
      setSelectedCell(null);
      fetchAttendance();
    } catch (err) {
      console.error('Delete presence error:', err);
    }
  };

  // ── Holiday CRUD ──
  const handleAddHoliday = async () => {
    if (!holidayForm.date || !holidayForm.label) return;
    try {
      await axios.post(`${API}/holidays`, { date: holidayForm.date, label: holidayForm.label, recurring: holidayForm.recurring, country: 'CI' }, { headers });
      setHolidayForm({ date: '', label: '', recurring: false });
      fetchHolidays();
      fetchAttendance();
    } catch (err) { console.error('Add holiday error:', err); }
  };

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await axios.delete(`${API}/holidays/${holidayId}`, { headers });
      fetchHolidays();
      fetchAttendance();
    } catch (err) { console.error('Delete holiday error:', err); }
  };

  const handleSeedHolidays = async () => {
    try {
      await axios.post(`${API}/holidays/seed-ci?year=${currentMonth.getFullYear()}`, {}, { headers });
      fetchHolidays();
      fetchAttendance();
    } catch (err) { console.error('Seed error:', err); }
  };

  // ── CSV Export (enriched) ──
  const handleExportCSV = () => {
    if (!data?.slots?.length) return;
    const rows = [['Poste', 'Véhicules', ...Array.from({ length: data.numDays }, (_, i) => i + 1)]];
    data.slots.forEach(slot => {
      const row = [slot.label, (slot.vehicles || []).join(', ')];
      slot.days.forEach(d => {
        let val = STATUS_CONFIG[d.status]?.label || d.status;
        if (d.runsCompleted) val += ` (${d.runsCompleted}/${d.runsExpected || '?'})`;
        if (d.manual) val += ' [Manuel]';
        if (d.reason) val += ` [${d.reason}]`;
        row.push(val);
      });
      rows.push(row);
    });
    // Per-slot KPIs
    rows.push([], ['--- KPIs par poste ---']);
    rows.push(['Poste', 'Jours attendus', 'Présent', 'Absent', 'Justifié', 'Manuel', 'Taux %']);
    data.slots.forEach(slot => {
      const k = slot.kpis || {};
      rows.push([slot.label, k.expected || 0, k.present || 0, k.absent || 0, k.justified || 0, k.manualPresences || 0, `${k.presenceRate || 0}%`]);
    });
    // Global KPIs
    rows.push([], ['--- KPIs globaux ---']);
    rows.push(['KPI', 'Valeur'],
      ['Jours ouvrés', data.kpis?.workingDays || 0],
      ['Présences', data.kpis?.present || 0],
      ['Absences', data.kpis?.absent || 0],
      ['Justifiées', data.kpis?.justified || 0],
      ['Taux présence', `${data.kpis?.presenceRate || 0}%`]
    );
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `presence_${contract?.contractNumber || contractId}_${monthStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Empty state ──
  const hasSlots = (contract?.truckSlots || []).length > 0;
  if (!hasSlots && !loading) {
    return (
      <Card className="border border-gray-200" data-testid="attendance-empty-state">
        <CardContent className="py-16 flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
            <CalendarClock className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-medium text-gray-900">Aucun poste véhicule configuré</p>
          <p className="text-sm text-gray-500 max-w-md">
            Configurez les postes dans l'onglet "Ressources" pour activer le suivi de présence.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="attendance-calendar">
      {/* ── Header: Month nav + actions ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={prevMonth} className="h-9 w-9 border-gray-300" data-testid="calendar-prev-month">
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </Button>
          <h3 className="text-xl font-semibold tracking-tight text-gray-900 min-w-[200px] text-center">
            {MONTHS_FR[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <Button variant="outline" size="icon" onClick={nextMonth} className="h-9 w-9 border-gray-300" data-testid="calendar-next-month">
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={batchMode ? "default" : "outline"} size="sm"
            onClick={() => { setBatchMode(!batchMode); setBatchSelection(new Set()); }}
            className={cn("gap-1.5", batchMode ? "bg-gray-900 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700")}
            data-testid="batch-mode-btn"
          >
            <ListChecks className="w-4 h-4" strokeWidth={1.5} />
            {batchMode ? 'Annuler sélection' : 'Sélection multiple'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowHolidayModal(true); fetchHolidays(); }}
            className="border-gray-300 gap-1.5 text-gray-700" data-testid="manage-holidays-btn">
            <Settings2 className="w-4 h-4" strokeWidth={1.5} /> Jours fériés
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!data?.slots?.length}
            className="border-gray-300 gap-1.5" data-testid="export-calendar-btn">
            <Download className="w-4 h-4" strokeWidth={1.5} /> Export CSV
          </Button>
        </div>
      </div>

      {/* ── Batch action bar ── */}
      {batchMode && (
        <div className="flex items-center justify-between p-3 bg-gray-900 text-white rounded-lg" data-testid="batch-action-bar">
          <span className="text-sm font-medium">
            {batchSelection.size} cellule{batchSelection.size > 1 ? 's' : ''} sélectionnée{batchSelection.size > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setBatchSelection(new Set())} className="h-8 text-xs gap-1">
              <X className="w-3 h-3" /> Tout désélectionner
            </Button>
            <Button size="sm" onClick={() => setShowBatchDialog(true)} disabled={batchSelection.size === 0}
              className="h-8 text-xs gap-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid="batch-justify-btn">
              <FileText className="w-3 h-3" /> Justifier les absences
            </Button>
          </div>
        </div>
      )}

      {/* ── Global KPI Cards ── */}
      {data?.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KPICard label="Jours ouvrés" value={data.kpis.workingDays} testId="kpi-working-days" />
          <KPICard label="Présences" value={data.kpis.present} color="emerald" testId="kpi-present" />
          <KPICard label="Absences" value={data.kpis.absent} color="red" testId="kpi-absent" />
          <KPICard label="Justifiées" value={data.kpis.justified} color="amber" testId="kpi-justified" />
          <KPICard label="Taux présence" value={`${data.kpis.presenceRate}%`}
            color={data.kpis.presenceRate >= 80 ? 'emerald' : data.kpis.presenceRate >= 50 ? 'amber' : 'red'}
            testId="kpi-presence-rate" />
        </div>
      )}

      {/* ── Per-slot KPIs ── */}
      {data?.slots?.some(s => s.kpis) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid="per-slot-kpis">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em] mb-3">Détail par poste</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.slots.map(slot => {
              const k = slot.kpis || {};
              return (
                <div key={slot.slotId} className="flex items-center justify-between bg-white border border-gray-200 px-4 py-3 rounded"
                  data-testid={`slot-kpi-${slot.slotId}`}>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{slot.label}</p>
                    <p className="text-xs text-gray-400 font-mono">{(slot.vehicles || []).join(', ')}</p>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <p className="text-xs text-gray-500">{k.present}/{k.expected} jours</p>
                      {k.manualPresences > 0 && <p className="text-[10px] text-blue-500">{k.manualPresences} manuel</p>}
                    </div>
                    <span className={cn('text-lg font-bold font-mono',
                      k.presenceRate >= 80 ? 'text-emerald-600' : k.presenceRate >= 50 ? 'text-amber-600' : 'text-red-600'
                    )}>{k.presenceRate}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Legend ── */}
      <div className="flex flex-wrap items-center gap-2" data-testid="status-legend">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <div key={key} className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border', cfg.bg, cfg.border, cfg.text)}
              data-testid={`status-legend-${key}`}>
              <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {cfg.label}
            </div>
          );
        })}
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border bg-blue-50 border-blue-400 text-blue-700">
          <UserCheck className="w-3.5 h-3.5" strokeWidth={2} /> Manuel
        </div>
      </div>

      {/* ── Calendar Grid ── */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-3 text-sm text-gray-500">Chargement...</span>
        </div>
      ) : error ? (
        <Card className="border-red-200"><CardContent className="py-8 text-center">
          <p className="text-red-600 text-sm">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={fetchAttendance}>Réessayer</Button>
        </CardContent></Card>
      ) : data?.slots?.length ? (
        <div className="overflow-x-auto border border-gray-200 bg-white relative" data-testid="attendance-grid">
          <table className="border-collapse text-left text-sm" style={{ minWidth: `${200 + data.numDays * 52}px` }}>
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-gray-50 border-b border-r-2 border-gray-300 px-4 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]"
                  style={{ width: '200px', minWidth: '200px', boxShadow: '2px 0 5px rgba(0,0,0,0.05)' }}>
                  Poste / Véhicule
                </th>
                {Array.from({ length: data.numDays }, (_, i) => {
                  const dayNum = i + 1;
                  const dateStr = `${monthStr}-${String(dayNum).padStart(2, '0')}`;
                  const dow = getDayOfWeek(dateStr);
                  const isWeekend = dow === 0 || dow === 6;
                  const isHoliday = data.holidays?.some(h => h.date === dateStr);
                  return (
                    <th key={dayNum} className={cn('border-b border-r border-gray-200 p-1 text-center',
                        isHoliday ? 'bg-orange-50' : isWeekend ? 'bg-gray-100' : 'bg-gray-50')}
                      style={{ width: '52px', minWidth: '52px' }}>
                      <div className="text-[10px] font-mono text-gray-400 leading-none tracking-wide">
                        {DAYS_SHORT[dow === 0 ? 6 : dow - 1]}
                      </div>
                      <div className={cn('text-xs font-mono leading-none mt-0.5 font-medium',
                        isHoliday ? 'text-orange-600' : isWeekend ? 'text-gray-400' : 'text-gray-700')}>
                        {dayNum}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.slots.map((slot) => (
                <tr key={slot.slotId} data-testid={`truck-row-${slot.slotId}`}>
                  <td className="sticky left-0 z-20 bg-white border-r-2 border-b border-gray-300 px-4 py-2"
                    style={{ width: '200px', minWidth: '200px', boxShadow: '2px 0 5px rgba(0,0,0,0.05)' }}>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900 truncate">{slot.label}</span>
                      <span className="text-[10px] text-gray-400 font-mono truncate">
                        {(slot.vehicles || []).join(', ') || 'Non assigné'}
                      </span>
                    </div>
                  </td>
                  {slot.days.map((day) => (
                    <CalendarCell
                      key={day.date} day={day} slot={slot}
                      onCellClick={handleCellClick}
                      batchMode={batchMode}
                      isSelected={batchSelection.has(`${slot.slotId}|${day.date}`)}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Card className="border-gray-200"><CardContent className="py-12 text-center">
          <CalendarClock className="w-10 h-10 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-gray-500">Aucune donnée pour ce mois.</p>
        </CardContent></Card>
      )}

      {/* ── Cell Details Dialog ── */}
      <Dialog open={!!selectedCell && !showAbsenceDialog && !showPresenceDialog} onOpenChange={(open) => !open && setSelectedCell(null)}>
        <DialogContent className="max-w-md" data-testid="cell-details-modal">
          <DialogHeader>
            <DialogTitle className="tracking-tight">{selectedCell?.day?.date}</DialogTitle>
            <DialogDescription>{selectedCell?.slot?.label} — {(selectedCell?.slot?.vehicles || []).join(', ') || 'Non assigné'}</DialogDescription>
          </DialogHeader>
          {selectedCell && (
            <div className="space-y-4 py-2">
              {(() => {
                const cfg = STATUS_CONFIG[selectedCell.day.status];
                const Icon = cfg?.icon;
                return (
                  <div className="flex items-center gap-2">
                    <Badge className={cn('px-2.5 py-1 text-xs border gap-1.5', cfg?.bg, cfg?.border, cfg?.text)}>
                      {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={2} />} {cfg?.label}
                    </Badge>
                    {selectedCell.day.manual && (
                      <Badge className="bg-blue-50 text-blue-700 border border-blue-300 text-xs gap-1">
                        <UserCheck className="w-3 h-3" /> Déclaré manuellement
                      </Badge>
                    )}
                  </div>
                );
              })()}

              {selectedCell.day.status === 'present' && !selectedCell.day.manual && (
                <div className="bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-800">
                  <strong>{selectedCell.day.runsCompleted}</strong> rotation(s) effectuée(s) sur{' '}
                  <strong>{selectedCell.day.runsExpected || 1}</strong> attendue(s)
                  {selectedCell.day.vehicle && <span className="block text-xs text-emerald-600 font-mono mt-1">{selectedCell.day.vehicle}</span>}
                </div>
              )}

              {selectedCell.day.status === 'present' && selectedCell.day.manual && (
                <div className="bg-blue-50 border border-blue-300 p-3 text-sm text-blue-800">
                  Présence déclarée manuellement
                  {selectedCell.day.vehicle && <span className="block text-xs text-blue-600 font-mono mt-1">Véhicule: {selectedCell.day.vehicle}</span>}
                  {selectedCell.day.note && <span className="block text-xs text-blue-600 mt-1">Note: {selectedCell.day.note}</span>}
                </div>
              )}

              {selectedCell.day.status === 'planned' && (
                <div className="bg-blue-50 border border-blue-300 p-3 text-sm text-blue-800">
                  <strong>{selectedCell.day.runsPlanned}</strong> rotation(s) planifiée(s)
                </div>
              )}

              {selectedCell.day.status === 'holiday' && (
                <div className="bg-orange-50 border border-orange-300 p-3 text-sm text-orange-800">
                  {selectedCell.day.label || 'Jour férié'}
                </div>
              )}

              {selectedCell.day.status === 'justified' && (
                <div className="bg-amber-50 border border-amber-300 p-3 space-y-1">
                  <p className="text-sm font-medium text-amber-800">
                    Motif : {ABSENCE_REASONS.find(r => r.value === selectedCell.day.reason)?.label || selectedCell.day.reason}
                  </p>
                  {selectedCell.day.justification && <p className="text-xs text-amber-700">{selectedCell.day.justification}</p>}
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-2 h-7 text-xs"
                    onClick={() => selectedCell.day.absenceId && handleDeleteAbsence(selectedCell.day.absenceId)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Supprimer la justification
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            {selectedCell?.day?.status === 'absent' && (
              <>
                <Button size="sm" className="bg-gray-900 text-white hover:bg-gray-800 gap-1.5"
                  onClick={() => setShowAbsenceDialog(true)} data-testid="justify-absence-button">
                  <FileText className="w-4 h-4" /> Justifier l'absence
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => setShowPresenceDialog(true)} data-testid="declare-presence-button">
                  <UserCheck className="w-4 h-4" /> Déclarer présent
                </Button>
              </>
            )}
            {selectedCell?.day?.status === 'present' && selectedCell.day.manual && (
              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                onClick={() => selectedCell.day.manualPresenceId && handleDeletePresence(selectedCell.day.manualPresenceId)}>
                <Trash2 className="w-3 h-3" /> Supprimer la présence manuelle
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setSelectedCell(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Absence Declaration Dialog ── */}
      <Dialog open={showAbsenceDialog} onOpenChange={(open) => { if (!open) setShowAbsenceDialog(false); }}>
        <DialogContent className="max-w-md" data-testid="absence-declaration-modal">
          <DialogHeader>
            <DialogTitle>Déclarer une absence justifiée</DialogTitle>
            <DialogDescription>{selectedCell?.slot?.label} — {selectedCell?.day?.date}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Motif</Label>
              <Select value={absenceForm.reason} onValueChange={(v) => setAbsenceForm(p => ({ ...p, reason: v, customReason: '' }))}>
                <SelectTrigger className="border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ABSENCE_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {absenceForm.reason === 'autre' && (
              <div className="space-y-2">
                <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Spécifier la raison *</Label>
                <Input className="border-gray-300" placeholder="Ex: Grève, Contrôle technique..."
                  value={absenceForm.customReason}
                  onChange={(e) => setAbsenceForm(p => ({ ...p, customReason: e.target.value }))} data-testid="custom-reason-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Commentaire (optionnel)</Label>
              <Textarea className="border-gray-300 resize-none" rows={3} placeholder="Détails supplémentaires..."
                value={absenceForm.justification}
                onChange={(e) => setAbsenceForm(p => ({ ...p, justification: e.target.value }))} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAbsenceDialog(false)}>Annuler</Button>
            <Button size="sm" className="bg-gray-900 text-white hover:bg-gray-800"
              onClick={handleDeclareAbsence}
              disabled={submittingAbsence || (absenceForm.reason === 'autre' && !absenceForm.customReason.trim())}>
              {submittingAbsence ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Manual Presence Dialog ── */}
      <Dialog open={showPresenceDialog} onOpenChange={(open) => { if (!open) setShowPresenceDialog(false); }}>
        <DialogContent className="max-w-md" data-testid="manual-presence-modal">
          <DialogHeader>
            <DialogTitle>Déclarer une présence manuelle</DialogTitle>
            <DialogDescription>{selectedCell?.slot?.label} — {selectedCell?.day?.date}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
              Le camion était physiquement présent mais aucun transfert n'a été enregistré dans le système.
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Plaque d'immatriculation (optionnel)</Label>
              <Input className="border-gray-300" placeholder="Ex: 3575LR01"
                value={presenceForm.vehiclePlate}
                onChange={(e) => setPresenceForm(p => ({ ...p, vehiclePlate: e.target.value }))} data-testid="presence-plate-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Note (optionnel)</Label>
              <Textarea className="border-gray-300 resize-none" rows={2} placeholder="Raison de la déclaration manuelle..."
                value={presenceForm.note}
                onChange={(e) => setPresenceForm(p => ({ ...p, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowPresenceDialog(false)}>Annuler</Button>
            <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 gap-1.5"
              onClick={handleDeclarePresence} disabled={submittingPresence} data-testid="confirm-presence-btn">
              {submittingPresence ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
              Confirmer la présence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Batch Absence Dialog ── */}
      <Dialog open={showBatchDialog} onOpenChange={(open) => { if (!open) setShowBatchDialog(false); }}>
        <DialogContent className="max-w-md" data-testid="batch-absence-modal">
          <DialogHeader>
            <DialogTitle>Justifier {batchSelection.size} absence{batchSelection.size > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>Le même motif sera appliqué à toutes les cellules sélectionnées.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Motif</Label>
              <Select value={batchForm.reason} onValueChange={(v) => setBatchForm(p => ({ ...p, reason: v, customReason: '' }))}>
                <SelectTrigger className="border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ABSENCE_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {batchForm.reason === 'autre' && (
              <div className="space-y-2">
                <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Spécifier la raison *</Label>
                <Input className="border-gray-300" placeholder="Ex: Grève..."
                  value={batchForm.customReason}
                  onChange={(e) => setBatchForm(p => ({ ...p, customReason: e.target.value }))} />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Commentaire (optionnel)</Label>
              <Textarea className="border-gray-300 resize-none" rows={3} placeholder="Détails..."
                value={batchForm.justification}
                onChange={(e) => setBatchForm(p => ({ ...p, justification: e.target.value }))} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowBatchDialog(false)}>Annuler</Button>
            <Button size="sm" className="bg-gray-900 text-white hover:bg-gray-800"
              onClick={handleBatchAbsences}
              disabled={submittingBatch || (batchForm.reason === 'autre' && !batchForm.customReason.trim())} data-testid="confirm-batch-btn">
              {submittingBatch ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Justifier ({batchSelection.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Holiday Management Modal ── */}
      <Dialog open={showHolidayModal} onOpenChange={setShowHolidayModal}>
        <DialogContent className="max-w-2xl" data-testid="holiday-management-modal">
          <DialogHeader>
            <DialogTitle>Gérer les jours fériés</DialogTitle>
            <DialogDescription>Ajoutez ou supprimez les jours fériés pour l'année {currentMonth.getFullYear()}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="bg-gray-50 border border-gray-200 p-4 space-y-3">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">Ajouter un jour férié</p>
              <div className="grid grid-cols-[1fr_1.5fr_auto] gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Date</Label>
                  <Input type="date" className="border-gray-300 h-9" value={holidayForm.date}
                    onChange={(e) => setHolidayForm(p => ({ ...p, date: e.target.value }))} data-testid="holiday-date-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Libellé</Label>
                  <Input className="border-gray-300 h-9" placeholder="Ex: Tabaski, Maouloud..." value={holidayForm.label}
                    onChange={(e) => setHolidayForm(p => ({ ...p, label: e.target.value }))} data-testid="holiday-label-input" />
                </div>
                <Button size="sm" className="bg-gray-900 text-white hover:bg-gray-800 h-9 px-4 gap-1.5"
                  onClick={handleAddHoliday} disabled={!holidayForm.date || !holidayForm.label} data-testid="add-holiday-btn">
                  <Plus className="w-4 h-4" /> Ajouter
                </Button>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em] mb-2">
                Jours fériés enregistrés ({holidays.length})
              </p>
              <div className="border border-gray-200 max-h-[320px] overflow-y-auto">
                {loadingHolidays ? (
                  <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
                ) : holidays.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-400">Aucun jour férié configuré</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider border-b w-[140px]">Date</th>
                        <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider border-b">Libellé</th>
                        <th className="px-4 py-2.5 border-b w-[50px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidays.map((h, idx) => (
                        <tr key={h.id || h.date} className={cn('hover:bg-gray-50 transition-colors', idx < holidays.length - 1 && 'border-b border-gray-100')}>
                          <td className="px-4 py-3 font-mono text-xs text-gray-700">{h.date}</td>
                          <td className="px-4 py-3 text-gray-900">
                            {h.label}
                            {h.recurring && <span className="text-gray-400 text-xs ml-2">(récurrent)</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {h.id && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteHoliday(h.id)} data-testid={`delete-holiday-${h.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleSeedHolidays}
              className="w-full border-dashed border-gray-300 gap-1.5 text-gray-500 hover:text-gray-700 hover:border-gray-400 h-10"
              data-testid="seed-holidays-btn">
              <Palmtree className="w-4 h-4" /> Importer les fériés Côte d'Ivoire ({currentMonth.getFullYear()})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Calendar Cell ──
const CalendarCell = ({ day, slot, onCellClick, batchMode, isSelected }) => {
  const cfg = STATUS_CONFIG[day.status];
  if (!cfg) return <td className="border-r border-b border-gray-200 p-0" style={{ width: '52px', minWidth: '52px', height: '52px' }} />;

  const Icon = cfg.icon;
  const isClickable = day.status !== 'rest' && day.status !== 'holiday';
  const isRest = day.status === 'rest';
  const showRunCount = day.status === 'present' && day.runsCompleted != null && !day.manual;
  const isManual = day.manual;

  return (
    <td className="border-r border-b border-gray-200 p-0" style={{ width: '52px', minWidth: '52px', height: '52px' }}
      data-testid={`calendar-cell-${slot.slotId}-${day.date}`}>
      <div
        className={cn(
          'relative w-full h-full flex flex-col items-center justify-center transition-all duration-200',
          cfg.cellClass,
          isClickable && 'cursor-pointer hover:opacity-80 active:scale-95',
          isRest && 'opacity-60',
          batchMode && isSelected && 'ring-2 ring-inset ring-gray-900',
          isManual && 'bg-blue-50 border-blue-400'
        )}
        style={isRest ? { background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, #E5E7EB 4px, #E5E7EB 6px)' } : undefined}
        onClick={() => isClickable && onCellClick(slot, day)}
        title={`${day.date}: ${cfg.label}${day.label ? ` - ${day.label}` : ''}${isManual ? ' (Manuel)' : ''}`}
      >
        {isManual ? (
          <UserCheck className="w-4 h-4 text-blue-500" strokeWidth={2} />
        ) : (
          <Icon className={cn('w-4 h-4', cfg.iconColor)} strokeWidth={2} />
        )}
        {showRunCount && (
          <span className="absolute top-0.5 right-0.5 bg-white/90 px-1 py-px text-[9px] font-bold font-mono text-emerald-700 border border-emerald-200 rounded shadow-sm leading-none"
            data-testid={`run-count-${slot.slotId}-${day.date}`}>
            {day.runsCompleted}/{day.runsExpected || '?'}
          </span>
        )}
        {batchMode && isSelected && (
          <div className="absolute top-0.5 left-0.5 w-3.5 h-3.5 bg-gray-900 rounded-sm flex items-center justify-center">
            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
          </div>
        )}
      </div>
    </td>
  );
};

// ── KPI Card ──
const KPICard = ({ label, value, color, testId }) => {
  const colorMap = { emerald: 'text-emerald-600', red: 'text-red-600', amber: 'text-amber-600' };
  return (
    <div className="bg-white border border-gray-200 p-4 flex flex-col gap-1 hover:border-gray-300 transition-colors" data-testid={testId}>
      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">{label}</span>
      <span className={cn('text-2xl font-bold tracking-tight font-mono', colorMap[color] || 'text-gray-900')}>{value}</span>
    </div>
  );
};

export default AttendanceCalendar;
